import { A } from '@adornis/base/env-info.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { switchMap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Category } from '../entities/category.js';
import { Participation } from '../entities/Participation.js';
import type { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { printComponent } from '../print-with-user-session.js';
import { lookupEntityById } from '../util.js';

export const getWorkshopParticipants = registerQuery({
  operationName: 'getWorkshopParticipants',
  type: () => [Participation],
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'state', type: () => [String] },
  ],
  resolve: (workshopID: string, state: ParticipationState[]) => {
    return async (gqlFields: BaseQLSelectionSet<Participation>) => {
      const participations = await loadMongoData(Participation, { productID: workshopID, state: { $in: state } });
      return participations;
    };
  },
});

export const generateParticipationSigningListPdf = registerMutation({
  type: () => String,
  operationName: 'generateParticipationSigningListPdf',
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<User>()({ _id: 1, _class: 1, firstName: 1, lastName: 1, title: 1 });
      if (!user) throw new Error('user not found');

      const workshop = await getByID(Workshop, workshopID)({ name: 1 });
      if (!workshop) throw new Error('workshop not found');

      const buffer = await printComponent('participation-signing-list', {
        user,
        timeout: 25 * 1000,
        queryParams: {
          workshopID,
        },
      });

      const name = `${workshop.name} - Teilnehmerzettel.pdf`;
      const file = await AdornisFile.createFromBuffer(buffer, name);

      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});

export const subscribeWorkshopsPaginated = registerSubscription({
  type: () => [Workshop],
  operationName: 'subscribeWorkshopsPaginated',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip, limit, filter) => (gqlFields: BaseQLSelectionSet<Workshop>) => {
    return watchEntireCollection('products-materialized').pipe(
      switchMap(() => getRawCollection('products-materialized')),
      switchMap(workshopCollection => {
        if (gqlFields.workshopSetName) {
          gqlFields.affiliation = { _class: 1, affiliation: 1, affiliationID: 1 };
        }

        const aggregation = [
          {
            $match: {
              _class: Workshop._class,
              ...(filter.institutionID ? { institutionID: filter.institutionID } : {}),
              ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
            },
          },
          { $lookup: lookupEntityById(Timeframe, 'timeframeID', 'timeframe') },
          { $set: { timeframe: { $arrayElemAt: ['$timeframe', 0] } } },
          { $lookup: lookupEntityById(Category, 'categoryID', 'category') },
          { $set: { category: { $arrayElemAt: ['$category', 0] } } },
          { $match: filter.getMongoFilter() },
          { $sort: filter.getMongoOptions().sort },
          { $project: gqlFields },
          { $skip: skip },
          { $limit: limit },
        ];

        return workshopCollection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray();
      }),
    );
  },
});
